/**
 * Token → CSS-custom-property bridge (doc 147 §8.9.1).
 *
 * `applyAppearance({mode, density, graphContrast})` is the canonical entry
 * point: it writes sharedTokens + palette[mode] + graphTokens[mode][contrast]
 * + density-derived tokens as `--<key>` custom properties on `<html>`, and
 * sets `data-mode`, `data-density`, `data-contrast` for CSS selectors and
 * accessibility-tool inspection.
 *
 * Components consume `var(--…)` — they never import the JS token map at
 * runtime (the constellation renderer being the one exception, which reads
 * the CSS vars via `getComputedStyle` to re-color on mode swaps).
 *
 * The older `injectTokens()` / `applyDensity()` exports remain for
 * back-compat with existing tests and the SSR-ish first-paint path (the
 * inline FOUC script in index.html); they delegate to `applyAppearance` with
 * defaults.
 */
import {
  dsPalettes,
  graphTokens,
  sharedTokens,
  type Mode,
  type GraphContrast,
  type TokenMap,
} from './tokens.js';
import { atlasOverrides } from './overrides.js';

// dsTokens alias removed — use dsPalettes/graphTokens directly

export type { Mode, GraphContrast };

/** DS v2 base (dark) with Atlas's power-user overrides — back-compat. */
export const tokens: TokenMap = { ...sharedTokens, ...dsPalettes.dark, ...atlasOverrides };

/** Density-mode row padding / body size / graph-node size (doc 147 §8.9.5). */
export type DensityMode = 'compact' | 'regular' | 'expanded';

export const densityScale: Record<DensityMode, { row: string; body: string; node: string }> = {
  // node size bumped from 16→20px (compact) — fixes the "tiny on a dense
  // graph" visibility problem from v1 (doc 147 §8.9.6 revised).
  compact: { row: '9px', body: '13px', node: '20px' },
  regular: { row: '14px', body: '14px', node: '22px' },
  expanded: { row: '18px', body: '16px', node: '30px' },
};

export interface Appearance {
  mode: Mode;
  density: DensityMode;
  graphContrast: GraphContrast;
}

export const DEFAULT_APPEARANCE: Appearance = {
  mode: 'dark',
  density: 'compact',
  graphContrast: 'standard',
};

/* ─── Internals ─────────────────────────────────────────────────────────── */

function writeMap(root: HTMLElement, map: TokenMap): void {
  for (const [key, value] of Object.entries(map)) {
    root.style.setProperty(`--${key}`, value);
  }
}

function writeDensity(root: HTMLElement, mode: DensityMode): void {
  const scale = densityScale[mode];
  root.style.setProperty('--sp-row', scale.row);
  root.style.setProperty('--fs-body', scale.body);
  root.style.setProperty('--graph-node', scale.node);
  root.setAttribute('data-density', mode);
}

/* ─── Public API ────────────────────────────────────────────────────────── */

/**
 * Apply a full appearance bundle. Idempotent — calling repeatedly with
 * different modes cleanly swaps every token (no leaked previous-mode values,
 * verified by `cssVariables.test.ts`).
 */
export function applyAppearance(
  appearance: Appearance,
  root: HTMLElement = document.documentElement,
): void {
  // 1. Shared (mode-invariant) tokens.
  writeMap(root, sharedTokens);
  // 2. Mode-dependent palette tokens.
  writeMap(root, dsPalettes[appearance.mode]);
  // 3. Atlas's documented overrides (§8.9.3 — fs-body, lh-table, sp-row,
  //    motion-fast, motion-std, density-default).
  writeMap(root, atlasOverrides);
  // 4. Graph rendering tokens for (mode, graphContrast).
  writeMap(root, graphTokens[appearance.mode][appearance.graphContrast]);
  // 5. Density-derived tokens (overrides sp-row / fs-body / graph-node).
  writeDensity(root, appearance.density);
  // 6. Attributes for CSS selectors and assistive tools.
  root.setAttribute('data-mode', appearance.mode);
  root.setAttribute('data-contrast', appearance.graphContrast);
}

/**
 * Legacy single-shot injection. New code should call `applyAppearance` with
 * a resolved appearance object (bootstrap.ts builds one from Firestore +
 * localStorage + matchMedia).
 */
export function injectTokens(root: HTMLElement = document.documentElement): void {
  applyAppearance(DEFAULT_APPEARANCE, root);
}

/** Apply a density mode in isolation (used by Settings density toggle). */
export function applyDensity(
  mode: DensityMode,
  root: HTMLElement = document.documentElement,
): void {
  writeDensity(root, mode);
}
